import {
  ACCOUNT_MANAGEMENT,
  ADMIN,
  ALL_ASSISTANTS,
  AUDIT_LOG,
  BACK_OFFICE,
  BRANCH_DEPLOYER,
  BUILDING_BLOCKS,
  CHANNEL_SETTINGS,
  CHAT_INSIGHTS,
  COLLECTION_PAGES,
  CORE_HYRO_KPIS,
  EXTERNAL_PREVIEW,
  INSIGHTS_DASHBOARD_NAMES,
  INTEGRATIONS,
  KNOWLEDGE_DEBUGGER,
  KNOWLEDGE_EXPLORER,
  PIPELINE_PERFORMANCE,
  SETTINGS,
  SKILLS,
  VOICE_INSIGHTS,
  AGENT_PERFORMANCE,
  INSIGHTS,
  CAMPAIGN_MANAGER,
  HYRO_ADMIN_PANEL_ACCOUNT_ID,
  DRAFTS,
  HOME,
  CONVERSATIONS,
  FAILURES,
  UTTERANCE_ORDER,
  CLOUD_CACHE_UPDATE,
  DATA_MAPPING,
  CONFIGURATION_EDITOR
} from "@consts";
import { isNotCodefullAgent, isProd } from "@utils";
import { Navigate, Outlet, Route, Routes, useLocation, useParams, useSearchParams } from "react-router-dom";
import React, { lazy, Suspense, useEffect, useLayoutEffect, useState } from "react";
import {
  assistantIdSelector,
  hasPermissionSelector,
  selectedAccountIdSelector,
  setSelectedAccountIdSelector,
  setPageNameSelector,
  setSwitchingTenantSelector,
  setSystemModeSelector,
  showHeaderSelector,
  systemModeSelector, userDataSelector,
  userPermissionsSelector,
  isOutboundAgentSelector
} from "../stores/selectors/appSelectors";
import AccountLayout from "./SubLayouts/AccountLayout";
import AccountManagement from "../components/widgets/account-management/AccountManagement";
import AdminLayout from "./SubLayouts/AdminLayout";
import AssistantInsights from "../pages/AssistantInsights";
import AssistantLayout from "./SubLayouts/AssistantLayout";
import { Box, GlobalStyles, useTheme } from "@mui/material";
import ForbiddenErrorPage from "./ErrorPages/forbidden-error-page/ForbiddenErrorPage";
import Header from "./Header";
import HyroLoader from "@hyro-components/HyroLoader";
import NotFoundErrorPage from "./ErrorPages/NotFoundErrorPage";
import { USER_PERMISSIONS } from "@hyro/dashboard-permissions";
import useAppStore from "../stores/appStore";
import { logout } from "../auth/frontegg";
import AuditLog from "../pages/audit-log/AuditLog";
import BranchDeployer from "../pages/branch-deployer/BranchDeployer";
import DraftPublishHeader from "./Header/header-types/status-header/DraftPublishing/DraftPublishHeader";
import {
  useDisplayDashboardSettingsBanner,
  useEffectAsync,
  useIsCurrentPageReadonly,
} from "@hooks";
import ReadOnlyHeader from "./Header/header-types/status-header/Permissions/ReadOnlyHeader";
import { SYSTEM_MODES } from "../stores/slices/createAppSlice";
import { resetCollectionsDataSelector } from "../stores/selectors/collectionsSelectors";
import DashboardSettingsBanner from "@hyro-components/DashboardSettingsBanner";
import { useFetchDashboardSettings } from "src/queries/hooks/settings";
import { accountsService } from "src/services";
import { useQueryClient } from "@tanstack/react-query";
import { FETCH_QUERY_KEYS } from "src/queries/consts";
import { useFetchAssistant } from "src/queries/hooks/assistants";
import { useAuthActions } from "@frontegg/react";
import { accountsSelector } from "src/stores/selectors/appSelectors";
import { getGlobalStyles } from "../theme/globalStyles";
import Failures from "src/pages/failures/Failures";
import UtteranceOrder from "../pages/UtteranceOrder";
import useNotificationsSocket from "src/components/widgets/notifications/useNotificationsSocket";
import { useFlags } from "launchdarkly-react-client-sdk";

const AllAssistants = lazy(() => import("../components/widgets/all-assistants/AllAssistants"));
const PipelinePerformance = lazy(() => import("../pages/PipelinePerformance"));
const ChannelSettings = lazy(() => import("../pages/Settings/Channel/ChannelSettings"));
const Conversation = lazy(() => import("../pages/Conversations/Conversation"));
const Conversations = lazy(() => import("../pages/Conversations/Conversations"));
const KnowledgeDebugger = lazy(() => import("../pages/KnowledgeDebugger"));
const KnowledgeExplorer = lazy(() => import("../pages/KnowledgeExplorer/KnowledgeExplorer"));
const CloudCacheUpdate = lazy(() => import("../pages/CloudCacheUpdate"));
const ConfigurationEditor = lazy(() => import("../pages/ConfigurationEditor"));
// eslint-disable-next-line no-secrets/no-secrets
const LookerInsightsDashboard = lazy(() => import("@hyro-components/LookerAdminInsightsDashboard"));
const Overview = lazy(() => import("../pages/Overview"));
const Drafts = lazy(() => import("../pages/drafts/Drafts"));
const DataMapping = lazy(() => import("../pages/data-mapping/DataMapping"));
const DataMappingTableView = lazy(() => import("../pages/data-mapping/DataMappingTableView"));
const DataMappingUtteranceGroupingView = lazy(() => import("../pages/data-mapping/utterance-grouping/UtteranceGroupingView"));
const Collections = lazy(() => import("../pages/Collections"));
const DashboardSettings = lazy(() => import("../pages/admin/Settings"));
const CollectionPage = lazy(() => import("../components/collections/assistant-configuration/CollectionPage"));
const RawDataPage = lazy(() => import("../components/collections/assistant-configuration/RawDataPage"));
const PolicyPage = lazy(() => import("../pages/policy/PolicyPage"));
const ExternalPreviewPage = lazy(() => import("../pages/ExternalPreviewPage"));
const CampaignManagerPage = lazy(() => import("../pages/account/campaign-manager/CampaignManager"));
const CampaignManagerLog = lazy(() => import("../components/widgets/campaign-manager/campaign-manager-log/CampaignManagerLog"));
const CampaignView = lazy(() => import("../components/widgets/campaign-manager/campaign-view/CampaignView"));
const AccountAssistants = lazy(() => import("../pages/AccountAssistants"));

const AccessAssistant = () => {
  const { assistantId, accountId } = useParams();
  const selectAssistantId = useAppStore(assistantIdSelector);
  const resetCollectionsData = useAppStore(resetCollectionsDataSelector);
  const { resetNotificationsState } = useAppStore();
  const queryClient = useQueryClient();
  const location = useLocation();
  const pageName = location.pathname.split("/")?.[3];

  const { enableNotificationsSocket } = useFlags();
  useNotificationsSocket(enableNotificationsSocket);

  useFetchAssistant({ accountId, assistantId });

  useLayoutEffect(() => {
    if (selectAssistantId !== assistantId) {
      resetCollectionsData();
      resetNotificationsState();

      // Clear collections cache
      queryClient.removeQueries({ queryKey: [FETCH_QUERY_KEYS.COLLECTIONS] });
      queryClient.removeQueries({ queryKey: [FETCH_QUERY_KEYS.COLLECTION] });
      queryClient.removeQueries({ queryKey: [FETCH_QUERY_KEYS.COLLECTION_REFS] });
      queryClient.removeQueries({ queryKey: [FETCH_QUERY_KEYS.VALIDATION_RESULTS] });
    }
  }, [resetCollectionsData, resetNotificationsState, assistantId, selectAssistantId, queryClient]);

  if (!selectAssistantId) return <HyroLoader height="100vh" />;
  if (!isNotCodefullAgent(assistantId) && COLLECTION_PAGES.includes(pageName)) {
    return <Navigate to={HOME} replace />;
  }
  return <AssistantLayout assistantId={assistantId} />;
};

const SelectAccount = () => {
  const userPermissions = useAppStore(userPermissionsSelector);
  const accounts = useAppStore(accountsSelector);
  const accountIds = accounts?.map(account => account.accountId);
  const DEFAULT_ACCOUNT_ID = import.meta.env.VITE_DEFAULT_ACCOUNT_ID;

  if (!accountIds) return <HyroLoader height="100vh" />;

  const accountToNavigateTo = accountIds.includes(DEFAULT_ACCOUNT_ID) ? DEFAULT_ACCOUNT_ID : accountIds[0];

  if (accountIds?.length === 0 || userPermissions?.permissions?.length === 0) {
    return logout({ returnTo: window.location.origin });
  }
  return <Navigate to={`/${accountToNavigateTo}`} replace />;
};

const AccessAccount = () => {
  const userPermissions = useAppStore(userPermissionsSelector);
  const { accountId } = useParams();
  const accountIds = useAppStore(accountsSelector)?.map(account => account.accountId);
  const location = useLocation();
  const setPageName = useAppStore(setPageNameSelector);

  useEffect(() => {
    setPageName(location.pathname.split("/")?.[3]);
  }, [location, setPageName]);

  if (!accountIds || !userPermissions?.permissions) return <HyroLoader height="100vh" />;

  if (accountIds.length === 0 || userPermissions?.permissions?.length === 0) {
    return logout({ returnTo: window.location.origin });
  }

  if (!accountIds.includes(accountId)) return <ForbiddenErrorPage />;

  return <AccountOutlet accountId={accountId} />;
};

const AccessAdminPanel = () => {
  const accountId = HYRO_ADMIN_PANEL_ACCOUNT_ID;
  const adminTenantId = import.meta.env.VITE_DEFAULT_ACCOUNT_ID;
  const selectedAccountId = useAppStore(selectedAccountIdSelector);
  const selectAccount = useAppStore(setSelectedAccountIdSelector);
  const setSystemMode = useAppStore(setSystemModeSelector);
  const systemMode = useAppStore(systemModeSelector);
  const dashboardSettings = useFetchDashboardSettings()?.data;
  const { switchTenant } = useAuthActions();
  const userData = useAppStore(userDataSelector);
  const setSwitchingTenant = useAppStore(setSwitchingTenantSelector);
  const switchingTenant = useAppStore(state => state.switchingTenant);

  useEffectAsync(async () => {
    if (selectedAccountId !== accountId) {
      selectAccount(accountId);
      if (userData?.tenantIds?.includes(adminTenantId) && userData?.tenantId !== adminTenantId) {
        setSwitchingTenant(true);
        await switchTenant({ tenantId: adminTenantId, silentReload: true });
        setSwitchingTenant(false);
      }
    }
    if (systemMode === SYSTEM_MODES.PREVIEW) setSystemMode(SYSTEM_MODES.READ_ONLY);
  }, [accountId, selectAccount, selectedAccountId, setSystemMode, systemMode, userData, switchTenant, setSwitchingTenant, adminTenantId]);

  if (switchingTenant) return <HyroLoader height="100vh" />;

  return <>
    {dashboardSettings?.banner?.show ? <DashboardSettingsBanner dashboardSettings={dashboardSettings} /> : null}
    <AdminLayout />
  </>;
};

const AccessOldDashboardRedirect = () => {
  const userPermissions = useAppStore(userPermissionsSelector);
  const { assistantId } = useParams();
  const [searchParams] = useSearchParams();
  const conversationId = searchParams.get("conversationId");
  const [selectedAccountId, setSelectedAccountId] = useState();
  const [error, setError] = useState(null);

  useEffectAsync(async () => {
    if (userPermissions?.permissions) {
      try {
        const accountIdRes = await accountsService.findAccountByAssistantId({ assistantId });
        setSelectedAccountId(accountIdRes.message);
      } catch (error) {
        console.error("Error finding account by assistant id", error);
        setError(true);
      }
    }
  }, [userPermissions, assistantId]);

  if (error) return <NotFoundErrorPage />;
  if (!userPermissions?.permissions || !selectedAccountId) return <HyroLoader height="100vh" />;
  return (<Navigate
    to={`/${selectedAccountId}/${assistantId}/conversations/${conversationId}`}
    replace
  />);
};

const AccountOutlet = ({ accountId }) => {
  const { assistantId } = useParams();
  const selectedAccountId = useAppStore(selectedAccountIdSelector);
  const selectAccount = useAppStore(setSelectedAccountIdSelector);
  const isPageReadonly = useIsCurrentPageReadonly();
  const systemMode = useAppStore(systemModeSelector);
  const { data: dashboardSettings } = useFetchDashboardSettings();
  const displaySettingsBanner = useDisplayDashboardSettingsBanner();
  const { switchTenant } = useAuthActions();
  const userData = useAppStore(userDataSelector);
  const setSwitchingTenant = useAppStore(setSwitchingTenantSelector);
  const switchingTenant = useAppStore(state => state.switchingTenant);

  useEffectAsync(async () => {
    if (selectedAccountId !== accountId) {
      selectAccount(accountId);
      if (userData?.tenantIds?.includes(accountId) && userData?.tenantId !== accountId) {
        setSwitchingTenant(true);
        await switchTenant({ tenantId: accountId, silentReload: true });
        setSwitchingTenant(false);
      }
    }
  }, [accountId, selectAccount, selectedAccountId]);

  if (!selectedAccountId || switchingTenant) return <HyroLoader height="100vh" />;

  return <>
    {displaySettingsBanner ? <DashboardSettingsBanner dashboardSettings={dashboardSettings} /> : <>
      {systemMode === SYSTEM_MODES.EDIT ? <>
        {(dashboardSettings?.banner?.show || (assistantId && !isPageReadonly)) && <DraftPublishHeader />}
        {isPageReadonly && <ReadOnlyHeader />}
      </> : null}
    </>}
    <Outlet />
  </>;
};

const WithCampaignManager = ({ children }) => {
  const accountId = useAppStore(selectedAccountIdSelector);
  const assistantId = useAppStore(assistantIdSelector);
  const isOutboundAgent = useAppStore(isOutboundAgentSelector);

  if (!isOutboundAgent) return (<Navigate to={`/${accountId}/${assistantId}`} replace />);

  return children;
};

const WithPermission = ({ permission, customRedirect, children }) => {
  const userPermissions = useAppStore(userPermissionsSelector);
  const hasPermission = useAppStore(hasPermissionSelector);
  const accountIds = useAppStore(state => state.accounts?.map(account => account.accountId) || []);

  if (!userPermissions?.permissions) return <HyroLoader height="100vh" />;

  if (accountIds?.length === 0 || userPermissions?.permissions?.length === 0) {
    return logout({ returnTo: window.location.origin });
  }
  if (customRedirect && !hasPermission(permission)) return <Navigate to={customRedirect} replace />;
  if (!hasPermission(permission)) return <ForbiddenErrorPage />;
  return children;
};

const MainLayout = () => {
  const location = useLocation();
  const showHeader = useAppStore(showHeaderSelector) && !location.pathname.includes(EXTERNAL_PREVIEW);
  const theme = useTheme();

  useEffect(() => {
    useAppStore.setState({ hasPageErrorType: null });
  }, [location.pathname]);

  return <Box>
    <GlobalStyles styles={getGlobalStyles(theme)} />
    {showHeader && <Header />}
    <Suspense fallback={<HyroLoader height="100vh" />}>
      <Routes>
        <Route index element={<SelectAccount />} />

        <Route path={EXTERNAL_PREVIEW} name="External Preview" element={
          <WithPermission permission={USER_PERMISSIONS.EXTERNAL_PREVIEW.READ} >
            <ExternalPreviewPage />
          </WithPermission>}
        />

        <Route
          path="/dashboard/:assistantId"
          name="Old Dashboard Redirect"
          element={<AccessOldDashboardRedirect />}
        />

        <Route path=":accountId" name="Account" element={<AccessAccount />}>
          <Route element={<AccountLayout />}>
            <Route index element={<WithPermission permission={USER_PERMISSIONS.ACCOUNT_ASSISTANTS_PAGE.READ}>
              <AccountAssistants />
            </WithPermission>} />
            <Route path={CAMPAIGN_MANAGER} element={<WithPermission permission={USER_PERMISSIONS.ACCOUNT_ASSISTANTS_PAGE.READ}>
              <CampaignManagerPage />
            </WithPermission>} />
            <Route path={`${CAMPAIGN_MANAGER}/:campaignId`} element={<CampaignView />} />
          </Route>

          <Route path=":assistantId" element={<AccessAssistant />}>
            <Route
              index
              element={(<WithPermission
                permission={USER_PERMISSIONS.HOME_PAGE.READ}
                customRedirect={CONVERSATIONS}
              >
                <Navigate to={HOME} replace />
              </WithPermission>)}
            />

            <Route
              path={HOME}
              name="Home"
              element={(<WithPermission
                permission={USER_PERMISSIONS.HOME_PAGE.READ}
              >
                <Overview />
              </WithPermission>)}
            />

            <Route
              path={INSIGHTS}
              name="Insights"
              element={(<WithPermission
                permission={USER_PERMISSIONS.INSIGHTS_PAGE.READ}
              >
                <AssistantInsights />
              </WithPermission>)}
            />
            <Route
              path={CONVERSATIONS + "/:conversationId"}
              name="Conversations"
              element={(<WithPermission
                permission={USER_PERMISSIONS.CONVERSATIONS_PAGE.READ}
              >
                <Conversation />
              </WithPermission>)}
            />
            <Route
              path={KNOWLEDGE_EXPLORER}
              name="Knowledge Explorer"
              element={(<WithPermission
                permission={USER_PERMISSIONS.KNOWLEDGE_EXPLORER_PAGE.READ}
              >
                <KnowledgeExplorer />
              </WithPermission>)}
            />
            <Route
              path={CONVERSATIONS}
              name="Conversations"
              element={(<WithPermission
                permission={USER_PERMISSIONS.CONVERSATIONS_PAGE.READ}
              >
                <Conversations />
              </WithPermission>)}
            />
            <Route
              path={CAMPAIGN_MANAGER}
              name="Campaign Manager"
              element={<WithCampaignManager>
                <CampaignManagerLog />
              </WithCampaignManager>}
            />
            <Route path={SKILLS}
              element={(<WithPermission
                permission={USER_PERMISSIONS.SKILLS_PAGE.READ}
              >
                <Collections writePermission={USER_PERMISSIONS.SKILLS_PAGE.WRITE} />
              </WithPermission>)}
            >
              <Route
                path={":collectionKey/:collectionInstanceKey"}
                element={(<WithPermission
                  permission={USER_PERMISSIONS.SKILLS_PAGE.READ}
                >
                  <CollectionPage writePermission={USER_PERMISSIONS.SKILLS_PAGE.WRITE} />
                </WithPermission>)}
              />
              <Route
                path={":collectionKey/:collectionInstanceKey/policy/:policyType/raw-data"}
                element={(<WithPermission
                  permission={USER_PERMISSIONS.SKILLS_PAGE.READ}
                >
                  <RawDataPage writePermission={USER_PERMISSIONS.SKILLS_PAGE.WRITE} />
                </WithPermission>)}
              />
              <Route
                path={":collectionKey/:collectionInstanceKey/policy/:policyType/:policyId?"}
                element={(<WithPermission
                  permission={USER_PERMISSIONS.SKILLS_PAGE.READ}
                >
                  <PolicyPage />
                </WithPermission>)}
              />
              <Route
                path={CHANNEL_SETTINGS}
                element={(<WithPermission
                  permission={USER_PERMISSIONS.SETTINGS_PAGE.READ}
                >
                  <ChannelSettings />
                </WithPermission>)}
              />
            </Route>
            <Route
              path={BUILDING_BLOCKS}
              element={<WithPermission
                permission={USER_PERMISSIONS.BUILDING_BLOCKS_PAGE.READ}
              >
                <Collections writePermission={USER_PERMISSIONS.BUILDING_BLOCKS_PAGE.WRITE} />
              </WithPermission>}>
              <Route
                path={":collectionKey/:collectionInstanceKey"}
                element={(<WithPermission
                  permission={USER_PERMISSIONS.BUILDING_BLOCKS_PAGE.READ}
                >
                  <CollectionPage writePermission={USER_PERMISSIONS.BUILDING_BLOCKS_PAGE.WRITE} />
                </WithPermission>)}>
              </Route>
            </Route>
            <Route
              path={INTEGRATIONS}
              name="Integrations - New!"
              element={<WithPermission
                permission={USER_PERMISSIONS.INTEGRATIONS_PAGE.READ}
              >
                <Collections writePermission={USER_PERMISSIONS.INTEGRATIONS_PAGE.WRITE} />
              </WithPermission>}>
              <Route
                path={":collectionKey/:collectionInstanceKey"}
                element={(<WithPermission
                  permission={USER_PERMISSIONS.INTEGRATIONS_PAGE.READ}
                >
                  <CollectionPage writePermission={USER_PERMISSIONS.INTEGRATIONS_PAGE.WRITE} />
                </WithPermission>)}>
              </Route>
            </Route>
            <Route
              path={CLOUD_CACHE_UPDATE}
              name="Cloud Cache Editor"
              element={(<WithPermission
                permission={USER_PERMISSIONS.CLOUD_CACHE_EDITOR_PAGE.READ}
              >
                <CloudCacheUpdate />
              </WithPermission>)}
            />
            <Route
              path={CONFIGURATION_EDITOR}
              name="Configuration Editor"
              element={(<WithPermission
                permission={USER_PERMISSIONS.CONFIGURATION_EDITOR_PAGE.READ}
              >
                <ConfigurationEditor />
              </WithPermission>)}
            />
            <Route
              path={AUDIT_LOG}
              name="Audit Log"
              element={(<WithPermission
                permission={USER_PERMISSIONS.AUDIT_LOG.READ}
              >
                <AuditLog />
              </WithPermission>)}
            />
            <Route
              path={BRANCH_DEPLOYER}
              name="Branch Deployer"
              element={(
                <WithPermission
                  permission={USER_PERMISSIONS.ADMIN_PANEL.READ}
                >
                  <BranchDeployer />
                </WithPermission>
              )}
            />
          </Route></Route>
        <Route
          path={ADMIN}
          name="Admin Panel"
          element={(<WithPermission permission={USER_PERMISSIONS.ADMIN_PANEL.READ}>
            <AccessAdminPanel />
          </WithPermission>)}
        >
          <Route index element={<Navigate to={ALL_ASSISTANTS} replace />} />
          <Route
            path={ALL_ASSISTANTS}
            element={<AllAssistants />}
          />
          <Route
            path={ACCOUNT_MANAGEMENT}
            name="Account Management"
            element={<AccountManagement />}
          />
          <Route
            path={AUDIT_LOG}
            name="Audit Log"
            element={<WithPermission permission={USER_PERMISSIONS.AUDIT_LOG.READ}>
              <AuditLog /></WithPermission>}
          />
          {!isProd() && <Route
            path={BRANCH_DEPLOYER}
            name="Branch Deployer"
            element={<WithPermission permission={USER_PERMISSIONS.ADMIN_PANEL.READ}>
              <BranchDeployer />
            </WithPermission>}
          />}
          <Route
            path={SETTINGS}
            element={<WithPermission permission={USER_PERMISSIONS.ADMIN_PANEL.READ}>
              <DashboardSettings />
            </WithPermission>}
          />
          <Route path={INSIGHTS} name="Insights" element={<Outlet />}>
            <Route index element={<Navigate to={VOICE_INSIGHTS} replace />} />
            <Route
              path={CORE_HYRO_KPIS}
              element={(<LookerInsightsDashboard
                dashboardName={INSIGHTS_DASHBOARD_NAMES.coreHyroKpis}
              />)}
            />
            <Route
              path={PIPELINE_PERFORMANCE}
              element={<PipelinePerformance />}
            />
            <Route
              path={CHAT_INSIGHTS}
              element={(<LookerInsightsDashboard
                dashboardName={INSIGHTS_DASHBOARD_NAMES.chat}
              />)}
            />
            <Route
              path={VOICE_INSIGHTS}
              element={(<LookerInsightsDashboard
                dashboardName={INSIGHTS_DASHBOARD_NAMES.voice}
              />)}
            />
            <Route
              path={AGENT_PERFORMANCE}
              element={(<LookerInsightsDashboard
                dashboardName={INSIGHTS_DASHBOARD_NAMES.agentPerformance}
              />)}
            />
          </Route>
          <Route path={BACK_OFFICE} name="Back office" element={<Outlet />}>
            <Route index element={<Navigate to={KNOWLEDGE_DEBUGGER} replace />} />
            <Route
              path={KNOWLEDGE_DEBUGGER}
              name="Knowledge Debugger"
              element={<KnowledgeDebugger />}
            />
            <Route
              path={KNOWLEDGE_EXPLORER}
              name="Knowledge Explorer"
              element={<KnowledgeExplorer />}
            />
            <Route
              path={DRAFTS}
              name="Drafts"
              element={<Drafts />}
            />
            <Route
              path={FAILURES}
              name="Failures"
              element={<Failures />}
            />
            <Route
              path={UTTERANCE_ORDER}
              name="Utterance Order"
              element={<WithPermission permission={USER_PERMISSIONS.ADMIN_PANEL.ORDER_COLLECTIONS.READ}><UtteranceOrder /></WithPermission>}
            />
            <Route
              path={DATA_MAPPING}
              name="Data Mapping"
              element={<WithPermission permission={USER_PERMISSIONS.DATA_MAPPING.READ}><DataMapping /></WithPermission>}
            >
              <Route
                path=":tableId/fields"
                name="Field Classification"
                element={<DataMappingTableView />}
              />
              <Route
                path=":tableId/utterances"
                name="Utterance Grouping"
                element={<DataMappingUtteranceGroupingView />}
              />
            </Route>
          </Route>
        </Route>

        <Route path="*" element={<NotFoundErrorPage />} />
      </Routes>
    </Suspense>
  </Box>;
};

export default MainLayout;
